import React from 'react';
import PropTypes from 'prop-types';

/**
 * A card component displaying the user's unit information
 * 
 * @param {Object} props - Component props
 * @param {Object} props.unit - Unit information object
 * @param {boolean} props.isPrepaid - Whether the unit is prepaid or postpaid
 * @param {boolean} props.isLoading - Whether the unit data is currently loading
 * @param {boolean} props.hasError - Whether there was an error loading the unit data
 * @param {boolean} props.hasActiveLevyCollection - Whether arrear levy collection is active on the unit
 */
const UnitCard = ({
  unit,
  isPrepaid = true,
  isLoading = false,
  hasError = false,
  hasActiveLevyCollection = false
}) => {
  // If we're loading, show a loading state
  if (isLoading) {
    return (
      <div className="card bg-beige-40 shadow-md rounded-lg p-6">
        <h2 className="hooray-dark-text text-sm font-semibold uppercase mb-2">Your Unit</h2>
        <div className="flex justify-center items-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
          <div className="ml-4 text-sm font-medium">Loading unit data...</div>
        </div>
      </div>
    );
  }
  
  // If there was an error or no unit data is provided, show an error state
  if (hasError || !unit) {
    return (
      <div className="card bg-beige-40 shadow-md rounded-lg p-6">
        <h2 className="hooray-dark-text text-sm font-semibold uppercase mb-2">Your Unit</h2>
        <div className="bg-red-50 p-4 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Failed to load unit data</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>Please try refreshing the page or contact support if the issue persists.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="card bg-beige-40 shadow-md rounded-lg p-6">
      <h2 className="hooray-dark-text text-sm font-semibold uppercase mb-2">Your Unit</h2>
      <div className="space-y-2">
        <div className="flex justify-between">
          <span className="text-gray-600">Unit:</span>
          <span className="font-semibold">{unit.displayName || unit.unitNumber || `Unit ${unit.unitId}`}</span>
        </div>
        {/* <div className="flex justify-between">
          <span className="text-gray-600">Unit ID:</span>
          <span className="font-semibold">{unit.unitId || 'Unknown'}</span>
        </div> */}
        <div className="flex justify-between">
          <span className="text-gray-600">Site:</span>
          <span className="font-semibold">{unit.siteName || 'Unknown'}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">Status:</span>
          <span className="font-semibold">
            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
              unit.status === 'ACTIVE' ? 'bg-green-100 text-green-800' :
              unit.status === 'INACTIVE' ? 'bg-red-100 text-red-800' :
              unit.status === 'VACANT' ? 'bg-yellow-100 text-yellow-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {unit.status || 'Unknown'}
            </span>
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">Billing Type:</span>
          <span className="font-semibold">
            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
              {isPrepaid ? 'Prepaid' : 'Postpaid'}
            </span>
          </span>
        </div>
        {hasActiveLevyCollection && (
          <div className="bg-amber-50 border-l-4 border-amber-400 text-amber-800 p-3 mt-4 rounded">
            <p className="text-sm font-semibold">Arrear levy collection active</p>
            <p className="mt-1 text-sm">This unit has an active arrear levy collection fee.</p>
          </div>
        )}
        {/* {unit.hardwareId && (
          <div className="flex justify-between">
            <span className="text-gray-600">Hardware ID:</span>
            <span className="font-semibold">{unit.hardwareId}</span>
          </div>
        )} */}
      </div>
    </div>
  );
};

UnitCard.propTypes = {
  unit: PropTypes.object,
  isPrepaid: PropTypes.bool,
  isLoading: PropTypes.bool,
  hasError: PropTypes.bool,
  hasActiveLevyCollection: PropTypes.bool
};

export default UnitCard; 